/**
 * Página "ProPoints" para futbolistas (vista del tutor)
 * COPIA EXACTA de PlayerProPoints.js adaptada para tutores
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import futbolistaService from '../../services/futbolistaService';
import { formatFechaPartido } from '../../utils/fechaUtils';

const FutbolistaProPoints = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [playerData, setPlayerData] = useState(null);
  const [historialProPoints, setHistorialProPoints] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(true);
  const [logros, setLogros] = useState(null);
  const [loadingLogros, setLoadingLogros] = useState(true);
  const [nivelData, setNivelData] = useState(null);
  const [loadingNivel, setLoadingNivel] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Estado para selector de temporada/torneo
  // selectedTorneoId: undefined = aún cargando; null = sin torneo (nuevo futbolista); number = torneo seleccionado
  const [temporadas, setTemporadas] = useState([]);
  const [selectedTemporadaId, setSelectedTemporadaId] = useState(null);
  const [selectedTorneoId, setSelectedTorneoId] = useState(undefined);
  const [loadingTorneos, setLoadingTorneos] = useState(true);

  // Función para obtener imagen según logro y género
  const getLogroImage = (nombreLogro, genero) => {
    const esFemenino = genero === 'F';

    // Normalizar nombre de logro
    const normalizedNombre = nombreLogro
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_');

    const imageMapMasculino = {
      'participacion': '/images/jugadorcampo-1x.jpeg',
      'victoria': '/images/jugadorcampo-1x.jpeg',
      'destacado': '/images/jugadorcampo-1x.jpeg',
      'el_jugon': '/images/eljugon-1x.jpeg',
      'el_cerrojo': '/images/cerrojo-1x.jpeg',
      'el_desequilibrante': '/images/desequilibrante-1x.jpeg',
      'el_lider': '/images/lider-1x.jpeg',
      'el_mejor_companero': '/images/mejorcompanero-1x.jpeg',
      'mayor_progresion': '/images/progresion-1x.jpeg',
      'constancia': '/images/constante-1x.jpeg',
      'ganador_incansable': '/images/incansable-1x.jpeg',
      'portero': '/images/portero-1x.jpeg'
    };

    const imageMapFemenino = {
      'participacion': '/images/la_jugadorcampo.png',
      'victoria': '/images/la_jugadorcampo.png',
      'destacado': '/images/la_jugadorcampo.png',
      'el_jugon': '/images/la_eljugon.png',
      'el_cerrojo': '/images/la_cerrojo.png',
      'el_desequilibrante': '/images/la_desequilibrante.png',
      'el_lider': '/images/la_lider.png',
      'el_mejor_companero': '/images/la_mejorcompanero.png',
      'mayor_progresion': '/images/la_progresion.png',
      'constancia': '/images/la_constante.png',
      'ganador_incansable': '/images/la_incansable.png',
      'portero': '/images/la_portero.png'
    };

    const imageMap = esFemenino ? imageMapFemenino : imageMapMasculino;
    const defaultImage = esFemenino ? '/images/la_jugadorcampo.png' : '/images/jugadorcampo-1x.jpeg';

    return imageMap[normalizedNombre] || defaultImage;
  };

  // Función para renderizar logro con imagen o texto (EXACTA como PlayerProPoints)
  const renderLogro = (logro, isMobile) => {
    const genero = playerData?.futbolista?.genero || 'M';
    const imageSrc = getLogroImage(logro.logro, genero);

    // Siempre mostrar como carta (tanto en móvil como desktop)
    return (
        <div key={logro.id} style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          margin: '8px'
        }}>
          <div style={{
            position: 'relative',
            width: isMobile ? '80px' : '90px',
            height: isMobile ? '80px' : '90px',
            borderRadius: '15px',
            background: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad)
              ? 'linear-gradient(135deg, #2C2C2C 0%, #1A1A1A 50%, #0D0D0D 100%)'
              : 'linear-gradient(135deg, #E8F5E8 0%, #C8E6C9 50%, #A5D6A7 100%)',
            border: `3px solid ${logro.cantidad > 0 ? '#4CAF50' : '#444444'}`,
            boxShadow: logro.cantidad > 0
              ? '0 8px 16px rgba(76, 175, 80, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
              : '0 8px 20px rgba(0,0,0,0.6), inset 0 2px 4px rgba(0,0,0,0.8), inset 0 -2px 4px rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}>
            {(logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? (
              // Carta con interrogante si no tiene el logro
              <div style={{
                fontSize: isMobile ? '3rem' : '3.5rem',
                color: '#CCCCCC',
                fontWeight: 'bold',
                textShadow: '0 0 10px rgba(255,255,255,0.3), 2px 2px 4px rgba(0,0,0,0.8)',
                opacity: '0.9',
                filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.2))'
              }}>
                ❓
              </div>
            ) : (
              // Imagen del logro si lo tiene
              <img
                src={imageSrc}
                alt={logro.logro}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  // Si la imagen no carga, mostrar icono alternativo
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            {/* Fallback si la imagen no carga */}
            <div style={{
              display: 'none',
              fontSize: '2rem',
              color: '#4CAF50',
              fontWeight: 'bold'
            }}>
              🏆
            </div>

          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '8px',
            position: 'relative',
            paddingRight: '12px'
          }}>
            <span style={{
              fontSize: '0.7rem',
              color: logro.cantidad > 0 ? '#2E7D32' : '#CCCCCC',
              textAlign: 'center',
              maxWidth: '90px',
              lineHeight: '1.2',
              fontWeight: logro.cantidad > 0 ? '600' : '500',
              textShadow: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none'
            }}>
              {logro.logro}
            </span>
            {logro.cantidad > 0 && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-20px',
                backgroundColor: '#FF4444',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                border: '2px solid white',
                boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                zIndex: 10
              }}>
                {logro.cantidad}
              </div>
            )}
          </div>
        </div>
      );
  };

  // Función para renderizar logro especial con imagen o texto (EXACTA como PlayerProPoints)
  const renderLogroEspecial = (logro, isMobile) => {
    const genero = playerData?.futbolista?.genero || 'M';
    const imageSrc = getLogroImage(logro.logro, genero);

    // Siempre mostrar como carta (tanto en móvil como desktop)
    return (
        <div key={logro.id} style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          margin: isMobile ? '4px' : '6px'
        }}>
          <div style={{
            position: 'relative',
            width: isMobile ? '72px' : '81px',
            height: isMobile ? '72px' : '81px',
            borderRadius: '15px',
            background: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad)
              ? 'linear-gradient(135deg, #2C2C2C 0%, #1A1A1A 50%, #0D0D0D 100%)'
              : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 50%, #FFCC02 100%)',
            border: `3px solid ${logro.cantidad > 0 ? '#FF9800' : '#444444'}`,
            boxShadow: logro.cantidad > 0
              ? '0 8px 16px rgba(255, 152, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
              : '0 8px 20px rgba(0,0,0,0.6), inset 0 2px 4px rgba(0,0,0,0.8), inset 0 -2px 4px rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}>
            {(logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? (
              // Carta con interrogante si no tiene el logro
              <div style={{
                fontSize: isMobile ? '3rem' : '3.5rem',
                color: '#CCCCCC',
                fontWeight: 'bold',
                textShadow: '0 0 10px rgba(255,255,255,0.3), 2px 2px 4px rgba(0,0,0,0.8)',
                opacity: '0.9',
                filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.2))'
              }}>
                ❓
              </div>
            ) : (
              // Imagen del logro si lo tiene
              <img
                src={imageSrc}
                alt={logro.logro}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  // Si la imagen no carga, mostrar icono alternativo
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            {/* Fallback si la imagen no carga */}
            <div style={{
              display: 'none',
              fontSize: '2rem',
              color: '#FF9800',
              fontWeight: 'bold'
            }}>
              ⭐
            </div>

          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '8px',
            position: 'relative',
            paddingRight: '12px'
          }}>
            <span style={{
              fontSize: '0.7rem',
              color: logro.cantidad > 0 ? '#E65100' : '#CCCCCC',
              textAlign: 'center',
              maxWidth: '90px',
              lineHeight: '1.2',
              fontWeight: logro.cantidad > 0 ? '600' : '500',
              textShadow: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none'
            }}>
              {logro.logro}
            </span>
            {logro.cantidad > 0 && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-20px',
                backgroundColor: '#FF4444',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                border: '2px solid white',
                boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                zIndex: 10
              }}>
                {logro.cantidad}
              </div>
            )}
          </div>
        </div>
      );
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Hook para manejar responsive design (EXACTO como PlayerProPoints)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar torneos del futbolista (para selector)
  useEffect(() => {
    const fetchTorneos = async () => {
      try {
        setLoadingTorneos(true);
        const response = await futbolistaService.getFutbolistaTorneos(id);
        if (response.success && response.data.length > 0) {
          setTemporadas(response.data);

          // Seleccionar temporada activa por defecto
          const tempActiva = response.data.find(t => t.activa) || response.data[0];
          setSelectedTemporadaId(tempActiva.id);

          // Seleccionar primer torneo activo (con jornadas pendientes) o el primero
          const torneoActivo = tempActiva.torneos.find(t => t.activo && t.inscripcion_activa) || tempActiva.torneos[0];
          // null explícito = sin torneo seleccionado (pero carga completada)
          setSelectedTorneoId(torneoActivo ? torneoActivo.id : null);
        } else {
          // Futbolista nuevo sin inscripciones: marcar como "cargado sin torneo"
          setSelectedTorneoId(null);
        }
      } catch (error) {
        console.error('Error al cargar torneos:', error);
      } finally {
        setLoadingTorneos(false);
      }
    };

    if (id) {
      fetchTorneos();
    }
  }, [id]);

  // Cargar datos del futbolista cuando cambia el torneo seleccionado
  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        const response = await futbolistaService.getFutbolistaProPoints(id, selectedTorneoId);
        if (response.success) {
          setPlayerData(response.data);
        }
      } catch (error) {
        console.error('Error al cargar datos del futbolista:', error);
      }
    };

    if (id && selectedTorneoId !== undefined) {
      fetchPlayerData();
    }
  }, [id, selectedTorneoId]);

  // Cargar historial detallado de ProPoints (depende del torneo seleccionado)
  useEffect(() => {
    const fetchHistorialProPoints = async () => {
      try {
        setLoadingHistorial(true);
        const token = localStorage.getItem('prokids_token');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const url = selectedTorneoId
          ? `/api/postgres/futbolistas/${id}/propoints?torneoId=${selectedTorneoId}`
          : `/api/postgres/futbolistas/${id}/propoints`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setHistorialProPoints(data.data.propoints);
          } else {
            setHistorialProPoints({ total: 0, temporada_actual: 0, ultima_jornada: 0, ranking_temporada: 0, ranking_general: 0, historial: [] });
          }
        } else {
          setHistorialProPoints({ total: 0, temporada_actual: 0, ultima_jornada: 0, ranking_temporada: 0, ranking_general: 0, historial: [] });
        }
      } catch (error) {
        console.error('Error al cargar historial:', error);
        setHistorialProPoints({ total: 0, temporada_actual: 0, ultima_jornada: 0, ranking_temporada: 0, ranking_general: 0, historial: [] });
      } finally {
        setLoadingHistorial(false);
      }
    };

    if (id && selectedTorneoId !== undefined) {
      fetchHistorialProPoints();
    }
  }, [id, selectedTorneoId]);

  // Cargar logros del futbolista (depende del torneo seleccionado)
  useEffect(() => {
    const fetchLogros = async () => {
      try {
        setLoadingLogros(true);

        const timeoutId = setTimeout(() => {
          setLogros({ logros_individuales: [], logros_especiales: [] });
          setLoadingLogros(false);
        }, 10000);

        const response = await futbolistaService.getFutbolistaLogros(id, selectedTorneoId);

        clearTimeout(timeoutId);

        if (response.success) {
          setLogros(response.data);
        } else {
          setLogros({ logros_individuales: [], logros_especiales: [] });
        }
      } catch (error) {
        console.error('Error al cargar logros:', error);
        setLogros({ logros_individuales: [], logros_especiales: [] });
      } finally {
        setLoadingLogros(false);
      }
    };

    if (id && selectedTorneoId !== undefined) {
      fetchLogros();
    }
  }, [id, selectedTorneoId]);

  // Cargar datos de nivel (depende del torneo seleccionado)
  useEffect(() => {
    const fetchNivelData = async () => {
      try {
        setLoadingNivel(true);
        const response = await futbolistaService.getFutbolistaNivel(id, selectedTorneoId);

        if (response.success) {
          setNivelData(response.data);
        } else {
          console.error('Error al cargar nivel:', response.message);
        }
      } catch (error) {
        console.error('💥 Error al cargar nivel:', error);
      } finally {
        setLoadingNivel(false);
      }
    };

    if (id && selectedTorneoId !== undefined) {
      fetchNivelData();
    }
  }, [id, selectedTorneoId]);

  // Estilos (EXACTOS como PlayerProPoints)
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#F0F2FF',
    padding: isMobile ? '15px' : '25px'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: isMobile ? '20px' : '30px',
    marginBottom: '25px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #E8ECFF'
  };

  const titleStyle = {
    fontSize: isMobile ? '1.8rem' : '2.2rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '30px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px'
  };

  const sectionTitleStyle = {
    fontSize: isMobile ? '1.4rem' : '1.6rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  if (!playerData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Cargando ProPoints...</p>
      </div>
    );
  }

  const propoints = playerData.propoints || {};
  const partidosHistorial = historialProPoints?.historial || [];

  return (
    <div style={containerStyle}>
      {/* Botón de volver */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/tutor')}
          style={{
            background: 'rgba(255, 255, 255, 0.9)',
            border: '2px solid #E8ECFF',
            borderRadius: '12px',
            color: '#061156',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}
        >
          ← Volver al Dashboard
        </button>
      </div>

      <h1 style={titleStyle}>
        ⭐ Sistema ProPoints™
      </h1>

      {/* Selector de Temporada y Torneo */}
      {temporadas.length > 0 && (
        <div style={{
          ...cardStyle,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '15px',
          padding: isMobile ? '15px' : '20px 25px',
          marginBottom: '20px',
          borderLeft: '4px solid #061156'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#061156', whiteSpace: 'nowrap' }}>
            🏆 Torneo:
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', flex: 1 }}>
            <select
              value={selectedTemporadaId || ''}
              onChange={(e) => {
                const tempId = parseInt(e.target.value);
                setSelectedTemporadaId(tempId);
                const temp = temporadas.find(t => t.id === tempId);
                if (temp && temp.torneos.length > 0) {
                  const torneoActivo = temp.torneos.find(t => t.activo && t.inscripcion_activa) || temp.torneos[0];
                  setSelectedTorneoId(torneoActivo.id);
                }
              }}
              style={{
                padding: '10px 14px',
                borderRadius: '10px',
                border: '2px solid #E8ECFF',
                fontSize: '14px',
                fontWeight: '500',
                color: '#061156',
                backgroundColor: 'white',
                cursor: 'pointer',
                minWidth: isMobile ? '100%' : '200px'
              }}
            >
              {temporadas.map(temp => (
                <option key={temp.id} value={temp.id}>
                  📅 {temp.nombre} {temp.activa ? '(Actual)' : ''}
                </option>
              ))}
            </select>

            {(() => {
              const tempSeleccionada = temporadas.find(t => t.id === selectedTemporadaId);
              if (!tempSeleccionada || tempSeleccionada.torneos.length === 0) return null;
              return (
                <select
                  value={selectedTorneoId || ''}
                  onChange={(e) => setSelectedTorneoId(parseInt(e.target.value))}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '2px solid #E8ECFF',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#061156',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    flex: 1,
                    minWidth: isMobile ? '100%' : '250px'
                  }}
                >
                  {tempSeleccionada.torneos.map(torneo => (
                    <option key={torneo.id} value={torneo.id}>
                      {torneo.activo ? '🟢' : '🔴'} {torneo.nombre} {torneo.inscripcion_activa ? '(Inscrito)' : ''}
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>
          {(() => {
            const tempSeleccionada = temporadas.find(t => t.id === selectedTemporadaId);
            const torneoSel = tempSeleccionada?.torneos.find(t => t.id === selectedTorneoId);
            if (!torneoSel || torneoSel.activo) return null;
            return (
              <div style={{
                backgroundColor: '#FFF3E0',
                color: '#E65100',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}>
                ⚠️ Torneo finalizado (histórico)
              </div>
            );
          })()}
        </div>
      )}

      {loadingTorneos && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '20px', marginBottom: '20px' }}>
          ⏳ Cargando torneos...
        </div>
      )}

      {/* Resumen de ProPoints */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📊 ProPoints de {playerData?.futbolista?.nombre}
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
          gap: '18px',
          marginBottom: '20px'
        }}>
          <div style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {propoints.total || 0}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              Total Acumulado
            </div>
          </div>

          <div style={{
            backgroundColor: '#2196F3',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)'
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {propoints.temporada_actual || 0}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              Esta Temporada
            </div>
          </div>

          <div style={{
            backgroundColor: '#FF9800',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(255, 152, 0, 0.3)'
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {propoints.ultima_jornada || 0}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              Última Jornada
            </div>
          </div>

          <div style={{
            backgroundColor: nivelData?.nivel_actual?.color || '#9C27B0',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: `0 4px 15px ${nivelData?.nivel_actual?.color ? nivelData.nivel_actual.color + '40' : 'rgba(156, 39, 176, 0.3)'}`
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {nivelData?.nivel_actual?.icono || '🏆'} {nivelData?.nivel_actual?.nivel || 'Inicial'}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              Nivel Actual
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: '#F8F9FF',
          padding: '15px',
          borderRadius: '10px',
          border: '2px solid #E8ECFF',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '1rem',
            color: '#4352A9',
            margin: 0,
            fontWeight: '500'
          }}>
            💡 Los ProPoints™ se ganan por esfuerzo, rendimiento y valores deportivos en cada jornada
          </p>
        </div>
      </div>

      {/* Logros */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          🏆 Logros
        </h2>

        {loadingLogros ? (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '2rem',
              marginBottom: '10px'
            }}>⏳</div>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              margin: 0,
              fontWeight: '500'
            }}>
              Cargando logros...
            </p>
          </div>
        ) : (
          <>
            {/* Logros Individuales */}
            <div style={{
              marginBottom: '30px'
            }}>
              <h3 style={{
                fontSize: isMobile ? '1.2rem' : '1.4rem',
                color: '#061156',
                fontWeight: '600',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🎯 Logros Individuales
              </h3>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'center' : 'flex-start',
                gap: '10px',
                padding: '20px',
                backgroundColor: '#F8F9FF',
                borderRadius: '12px',
                border: '2px solid #E8ECFF'
              }}>
                {(logros?.logros_individuales && logros.logros_individuales.length > 0) ?
                  logros.logros_individuales.map((logro) => renderLogro(logro, isMobile)) :
                  // Mostrar cartas vacías si no hay logros
                  [
                    { id: 1, logro: 'Participación', cantidad: 0 },
                    { id: 2, logro: 'victoria', cantidad: 0 },
                    { id: 3, logro: 'destacado', cantidad: 0 },
                    { id: 5, logro: 'El Jugón', cantidad: 0 },
                    { id: 6, logro: 'El Cerrojo', cantidad: 0 },
                    { id: 7, logro: 'El Desequilibrante', cantidad: 0 },
                    { id: 8, logro: 'El Líder', cantidad: 0 },
                    { id: 9, logro: 'El Mejor Compañero', cantidad: 0 }
                  ].map((logro) => renderLogro(logro, isMobile))
                }
              </div>
            </div>

            {/* Logros Especiales */}
            <div>
              <h3 style={{
                fontSize: isMobile ? '1.2rem' : '1.4rem',
                color: '#061156',
                fontWeight: '600',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ⭐ Logros Especiales
              </h3>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'center' : 'flex-start',
                gap: '10px',
                padding: '20px',
                backgroundColor: '#FFF8E1',
                borderRadius: '12px',
                border: '2px solid #FFE082'
              }}>
                {/* Logros especiales REALES del backend */}
                {(logros?.logros_especiales && logros.logros_especiales.length > 0) ?
                  logros.logros_especiales.map((logro) => renderLogroEspecial(logro, isMobile)) :
                  // Mostrar cartas vacías si no hay logros especiales
                  [
                    { id: 1, logro: 'Mayor Progresión', cantidad: 0 },
                    { id: 2, logro: 'Constancia', cantidad: 0 },
                    { id: 3, logro: 'Ganador Incansable', cantidad: 0 }
                  ].map((logro) => renderLogroEspecial(logro, isMobile))
                }
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mensaje de temporada no activa */}
      {nivelData && nivelData.en_temporada_activa === false && (
        <div style={{
          backgroundColor: '#FFF3E0',
          border: '2px solid #FFB74D',
          borderRadius: '15px',
          padding: '20px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: isMobile ? '1.1rem' : '1.2rem',
            fontWeight: '700',
            color: '#E65100',
            marginBottom: '10px'
          }}>
            ⚠️ Futbolista no inscrito en temporada activa
          </div>
          <div style={{
            fontSize: isMobile ? '0.9rem' : '1rem',
            color: '#E65100',
            marginBottom: '15px'
          }}>
            Este futbolista está inscrito en: <strong>{nivelData.temporada}</strong>
          </div>
          <div style={{
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            color: '#666'
          }}>
            Los datos mostrados corresponden a su última temporada de participación.
          </div>
        </div>
      )}

      {/* Progreso de Nivel */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          🎯 Nivel del Futbolista
        </h2>

        {nivelData ? (
          <div>
            {/* Nivel Actual */}
            <div style={{
              backgroundColor: '#F8F9FF',
              padding: isMobile ? '20px' : '25px',
              borderRadius: '15px',
              border: '2px solid #E8ECFF',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '15px',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    lineHeight: 1
                  }}>
                    {nivelData.nivel_actual?.icono || '🏆'}
                  </div>
                  <div>
                    <div style={{
                      fontSize: isMobile ? '1.3rem' : '1.5rem',
                      fontWeight: '700',
                      color: nivelData.nivel_actual?.color || '#061156',
                      marginBottom: '2px'
                    }}>
                      {nivelData.nivel_actual?.nivel || 'Inicial'}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {nivelData.nivel_actual?.descripcion || 'Nivel de inicio'}
                    </div>
                  </div>
                </div>
                <div style={{
                  textAlign: 'right'
                }}>
                  <div style={{
                    fontSize: isMobile ? '1.1rem' : '1.3rem',
                    fontWeight: '700',
                    color: '#4CAF50',
                    marginBottom: '2px'
                  }}>
                    {nivelData.propoints_acumulados || 0} PP
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    color: '#666',
                    fontWeight: '500'
                  }}>
                    ProPoints Acumulados
                  </div>
                </div>
              </div>

              {/* Progreso hacia siguiente nivel */}
              {nivelData.nivel_actual && nivelData.siguiente_nivel ? (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      Progreso hacia {nivelData.siguiente_nivel.icono} {nivelData.siguiente_nivel.nivel}
                    </span>
                    <span style={{
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#666',
                      fontWeight: '600'
                    }}>
                      {Math.round(nivelData.progreso)}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: '#E0E0E0',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: `${nivelData.progreso}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${nivelData.siguiente_nivel.color || '#4CAF50'}, ${nivelData.siguiente_nivel.color || '#4CAF50'}CC)`,
                      borderRadius: '6px',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    color: '#666',
                    textAlign: 'center'
                  }}>
                    Necesita <strong>{nivelData.puntos_para_siguiente}</strong> ProPoints más para alcanzar el siguiente nivel
                  </div>
                </div>
              ) : nivelData.nivel_actual ? (
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  backgroundColor: '#FFF3E0',
                  borderRadius: '10px',
                  border: '2px solid #FFB74D'
                }}>
                  <div style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '700',
                    color: '#E65100',
                    marginBottom: '5px'
                  }}>
                    🏆 ¡Nivel Máximo Alcanzado!
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    color: '#E65100'
                  }}>
                    Ha alcanzado el nivel más alto disponible
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  backgroundColor: '#F5F5F5',
                  borderRadius: '10px',
                  border: '2px solid #BDBDBD'
                }}>
                  <div style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '700',
                    color: '#666',
                    marginBottom: '5px'
                  }}>
                    ℹ️ Sin datos de nivel
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    color: '#666'
                  }}>
                    {nivelData.mensaje || 'No hay información de nivel disponible'}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#666'
          }}>
            {loadingNivel ? (
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
                <p>Cargando información de nivel...</p>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>❓</div>
                <p>No se pudo cargar la información del nivel</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Historial de Partidos */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📈 Historial de Partidos
        </h2>

        {loadingHistorial ? (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '2rem',
              marginBottom: '10px'
            }}>⏳</div>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              margin: 0,
              fontWeight: '500'
            }}>
              Cargando historial...
            </p>
          </div>
        ) : partidosHistorial && partidosHistorial.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {partidosHistorial.map((partido, index) => (
              <div key={index} style={{
                backgroundColor: '#F8F9FF',
                borderRadius: '12px',
                padding: '20px',
                border: '2px solid #E8ECFF',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <h4 style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: '#061156',
                    margin: 0
                  }}>
                    Jornada {partido.jornada_numero}
                  </h4>
                  <div style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    {partido.total_puntos || 0} pts
                  </div>
                </div>

                <div style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  marginBottom: '10px'
                }}>
                  📅 {formatFechaPartido(partido.fecha_partido, partido.jornada_fecha, 'corto')}
                </div>

                <div style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  marginBottom: '15px'
                }}>
                  📍 {partido.sede_nombre}
                </div>

                {partido.logros && partido.logros.length > 0 && (
                  <div>
                    <h5 style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#061156',
                      marginBottom: '8px'
                    }}>
                      Logros obtenidos:
                    </h5>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '5px'
                    }}>
                      {partido.logros.map((logro, logroIndex) => (
                        <span key={logroIndex} style={{
                          backgroundColor: '#E8F5E8',
                          color: '#2E7D32',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {logro.nombre} (+{logro.puntos})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '60px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px dashed #DDD'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '15px'
            }}>📊</div>
            <h4 style={{
              color: '#666',
              fontSize: '1.2rem',
              fontWeight: '600',
              margin: 0
            }}>
              No hay historial de partidos disponible
            </h4>
          </div>
        )}
      </div>
    </div>
  );
};

export default FutbolistaProPoints;
